/**
 * @file: NotificationCenter.jsx
 * @status: Sovereign Horizontal Radar (V12.6.1) - COMPACT & STEALTH & ACCESSIBILITY ENHANCED
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: تحديث المقاسات للرؤية الكاملة 100%؛ إضافة id/name لتعزيز التتبع؛ الحفاظ على بروتوكول الحجب الذكي بختم @CEO.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bell, Clock, CheckCircle, ShieldAlert, 
  RefreshCw, Zap, Database, Lock, EyeOff, Terminal
} from 'lucide-react';
import { Badge } from 'antd';

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // استخراج الرتبة السيادية من الذاكرة العميقة للمتصفح
  const userRole = localStorage.getItem('huger_user_role');
  const API_URL = "https://portal.huger.sa/api.php";

  useEffect(() => {
    fetchLiveNotifications();
    const interval = setInterval(fetchLiveNotifications, 30000); 
    return () => clearInterval(interval);
  }, [userRole]);

  const fetchLiveNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}?action=get_identities&nocache=${Date.now()}`);
      if (Array.isArray(res.data)) {
        const liveNotes = res.data.slice(0, 6).map(user => {
          
          const isCEO = userRole === 'CEO';
          // بروتوكول توليد الهوية المستعارة (Anonymity Protocol)
          const maskedId = `HGR-${(user.id * 1337).toString(16).toUpperCase().slice(-4)}`;
          const displayName = isCEO ? user.name : `Entity [${maskedId}]`;
          const isActive = user.status === 'Active';
          
          return {
            id: user.id,
            text: `رصد نشاط هوية: ${displayName}`,
            subText: isCEO ? `Position: ${user.role}` : 'Confidential Access Level',
            status: isActive ? 'success' : 'alert'
          };
        });
        setNotifications(liveNotes);
      }
    } catch (err) {
      console.error("Critical: Radar Link Severed");
    } finally {
      setLoading(false);
    }
  };

  if (!notifications.length && !loading) return null;

  return (
    <div 
      id="huger_sovereign_status_radar"
      name="notification_radar_bar"
      className="w-full bg-[#050B14]/90 backdrop-blur-3xl border-b border-white/5 py-2.5 overflow-hidden relative group transition-all duration-700 select-none z-50" 
      dir="rtl"
    >
      
      {/* تأثير التدرج الجانبي لإخفاء حواف النص (Edge Fading) */}
      <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#050B14] to-transparent z-10 pointer-events-none"></div>
      <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#050B14] to-transparent z-10 pointer-events-none"></div>

      <div className="max-w-[1440px] mx-auto px-6 flex items-center gap-6">
        
        {/* بلوك العنوان الثابت (Compact) */}
        <div id="radar_title_block" className="flex items-center gap-3 z-20 flex-shrink-0 border-l border-white/10 pl-6">
          <div className="bg-gradient-to-br from-[#F57C00]/20 to-transparent p-1.5 rounded-lg border border-[#F57C00]/30 shadow-lg">
            <Terminal size={14} className="text-[#F57C00] animate-pulse" /> 
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-white uppercase italic tracking-tighter flex items-center gap-1">
              رادار الحالة <div className="w-1 h-1 bg-[#F57C00] rounded-full animate-ping"></div>
            </span>
            <span className="text-[7px] text-slate-600 font-black uppercase tracking-widest opacity-60 leading-none">HUGER V-RADAR</span>
          </div>
        </div>

        {/* الشريط المتحرك المطور (Sovereign Stream) */}
        <div id="sovereign_stream_container" className="flex-1 overflow-hidden relative scale-95 origin-right">
          <div className="flex items-center gap-16 animate-marquee whitespace-nowrap">
            {notifications.map((note, index) => (
              <div key={`${note.id}-${index}`} id={`note_item_${note.id}`} className="flex items-center gap-4 group/item transition-opacity hover:opacity-100">
                <div className={`p-1 rounded-full ${note.status === 'success' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  {note.status === 'success' ? 
                    <CheckCircle size={10} className="text-emerald-500" /> : 
                    <ShieldAlert size={10} className="text-amber-500" />
                  }
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-black text-slate-300 group-hover/item:text-[#F57C00] transition-colors flex items-center gap-1.5 tracking-tight italic">
                    {note.text}
                    {userRole !== 'CEO' && <Lock size={9} className="text-slate-700 opacity-40"/>}
                  </span>
                  <span className="text-[7px] text-slate-600 font-black uppercase tracking-widest mt-0.5">{note.subText}</span>
                </div>
                <span className="text-white/5 font-black text-sm select-none mx-2">||</span>
              </div>
            ))}
          </div>
        </div>

        {/* مؤشر المزامنة السيادي - Compact */}
        <div id="sync_indicator_block" className="flex items-center gap-4 z-20 flex-shrink-0 border-r border-white/10 pr-6">
          {loading ? (
            <RefreshCw size={12} className="animate-spin text-[#F57C00]" />
          ) : (
            <div id="sync_status_active" className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-xl border border-white/5 shadow-inner">
               <div className="relative flex items-center">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping absolute inset-0 opacity-50"></div>
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full relative"></div>
               </div>
               <span className="text-[8px] text-emerald-500 font-black uppercase tracking-widest italic leading-none">Sync</span>
            </div>
          )}
          <div className="hidden xl:flex flex-col items-end opacity-30">
             <span className="text-[7px] text-slate-500 font-black uppercase tracking-widest leading-none">@CEO Guard</span>
             <Database size={9} className="text-slate-600 mt-1"/>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(30%); }
          100% { transform: translateX(-150%); }
        }
        .animate-marquee {
          display: flex;
          animation: marquee 40s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default NotificationCenter;